import { bcApi } from "../services/apiClients";
import { formatAxiosError } from "./common";
import { getActiveEnvironment } from "../services/environmentService";
import { getCompanyByName } from "../services/companiesService";
import { encodeBcCompanyName } from "./stringNormalization";
import enviroment from "../config/env";

export const getCurrentEnvironment = async () => {
	try {
		const activeEnv = await getActiveEnvironment();
		return { ...activeEnv, "bcBaseUrl": enviroment.bcBaseUrl } || enviroment;
	} catch (error) {
		console.warn("No se pudo obtener configuración activa, usando predeterminada:", error);
		return enviroment;
	}
};

// No es la forma correcta de manejarlo, en un mundo ideal si BC rompe devuelve un código distinto a 200
// Si falla Business Central a veces devuelve 200 y el flujo sigue normalmente! No permitiendome manejar el error como se debe
// Esta función es el último bastión para chequear si da error y permitirnos manejarlo
export const hasError = (value) => {
	if (typeof value === "string" && value.toLowerCase().startsWith("error:")) {
		return true;
	}
	return false;
};

const checkValueForErrors = (value) => {
	if (typeof value === "string" && value.toLowerCase().startsWith("error:")) {
		const errorMessage = value.substring(6).trim();
		console.error("Error de Business Central:", errorMessage);
		alert(`Error en Business Central: ${errorMessage}`);
		return true;
	}
	return false;
};

// --- Fetch helpers ---

export const fetchFromBusinessCentral = async (endpoint) => {
	try {
		const env = await getCurrentEnvironment();
		const encodedCompany = encodeBcCompanyName(env.company);
		const url = `${env.bcBaseUrl}/${env.env}/ODataV4/Company('${encodedCompany}')/${endpoint}`;

		const response = await bcApi.get(url);
		const value = response.data.value;

		if (checkValueForErrors(value)) return "";
		return value || [];
	} catch (error) {
		console.error(`Error al obtener datos de Business Central (${endpoint}):`, error);
		throw error;
	}
};

export const fetchFromBusinessCentralEspecial = async (endpoint, data = {}) => {
	try {
		const env = await getCurrentEnvironment();
		const encodedCompany = encodeBcCompanyName(env.company);
		const url = `${env.bcBaseUrl}/${env.env}/ODataV4/${endpoint}?company='${encodedCompany}'`;

		const response = await bcApi.post(url, data);
		const value = response.data.value;

		if (checkValueForErrors(value)) return "";
		return value || [];
	} catch (error) {
		console.error(`Error al obtener datos de Business Central (${endpoint}):`, error);
		throw error;
	}
};

export const sendToBusinessCentral = async (endpoint, data, isCustomEndpoint = false) => {
	try {
		const env = await getCurrentEnvironment();
		const encodedCompany = encodeBcCompanyName(env.company);
		const BC_BASE_URL = `${env.bcBaseUrl}/${env.env}/ODataV4`;

		const url = isCustomEndpoint
			? `${BC_BASE_URL}/${endpoint}?company='${encodedCompany}'`
			: `${BC_BASE_URL}/Company('${encodedCompany}')/${endpoint}`;

		const response = await bcApi.post(url, data);
		const responseData = response.data;

		if (responseData.value && checkValueForErrors(responseData.value)) {
			return { ...responseData, value: "" };
		}

		return responseData;
	} catch (error) {
		console.error(`Error al enviar datos a Business Central (${endpoint}):`, error);
		throw error;
	}
};

export const fetchInvoiceHistory = async (filter) => {
	try {
		const env = await getCurrentEnvironment();
		const encodedCompany = encodeBcCompanyName(env.company);

		const url = `${env.bcBaseUrl}/${env.env}/ODataV4/Company('${encodedCompany}')/salesInvoice?${filter}`;

		const response = await bcApi.get(url);
		return response.data?.value || [];
	} catch (error) {
		console.error(`Error al obtener facturas históricas:`, error);
		throw "No se pudieron obtener las facturas históricas. Inténtelo nuevamente más tarde.";
	}
};

export const getCompanies = async () => {
	try {
		const env = await getCurrentEnvironment();
		const url = `${env.bcBaseUrl}/${env.env}/api/v2.0/companies`;

		const response = await bcApi.get(url);
		return response.data?.value || [];
	} catch (error) {
		throw "No se pudieron obtener las empresas.";
	}
};

//Metodo para contabilizar una factura en el momento de sincronizar ( Paraguay y Honduras)
export const postInvoice = async (systemId, isCreditMemo) => {
	try {
		const env = await getCurrentEnvironment();
		const company = await getCompanyByName(env.company);

		const baseUrl = `${env.bcBaseUrl}/${env.env}/api/v2.0/companies(${company.bcID})`;
		const postInvoiceUrl = baseUrl + `/salesInvoices(${systemId})/Microsoft.NAV.post`;
		const postCreditMemoUrl = baseUrl + `/salesCreditMemos(${systemId})/Microsoft.NAV.postAndSend`;

		const url = isCreditMemo ? postCreditMemoUrl : postInvoiceUrl;

		const response = await bcApi.post(url);
		if (response.status == 204) { return true; }
		else {
			let message = response.data?.error?.message ||
				response.data?.error?.code ||
				`No se pudo contabilizar la factura. ${response?.message}`;
			window.messageApi.error(message,
				{
					modal: true,
					title: "Error Business Central"
				});
			return false;
		}
	} catch (error) {
		let message = formatAxiosError(error, `No se pudo contabilizar la factura. ${error?.message}`);

		window.messageApi.error(message, {
			modal: true,
			title: "Error Business Central"
		});
		return false;
	}
};

export const downloadInvoicePdfFromBC = async ({
	invoiceSystemId,
	fileName = "Factura.pdf",
}) => {
	try {
		const env = await getCurrentEnvironment();
		const company = await getCompanyByName(env.company);

		const url =
			`${env.bcBaseUrl}/${env.env}/api/v2.0` +
			`/companies(${company.bcID})` +
			`/salesInvoices(${invoiceSystemId})` +
			`/pdfDocument/pdfDocumentContent`;

		const response = await bcApi.get(url, {
			responseType: "blob",
		});

		// BC puede devolver 200 con error en texto
		if (
			response.data instanceof Blob &&
			response.data.type === "application/json"
		) {
			const text = await response.data.text();
			if (hasError(text)) {
				const message = formatAxiosError("No se pudo descargar la factura.");

				window.messageApi.error(message, {
					modal: true,
					title: "Error Business Central"
				});

				return false;
			}
		}

		// Descargar archivo
		const blob = new Blob([response.data], { type: "application/pdf" });
		const downloadUrl = window.URL.createObjectURL(blob);

		const link = document.createElement("a");
		link.href = downloadUrl;
		link.download = fileName;
		document.body.appendChild(link);
		link.click();

		document.body.removeChild(link);
		window.URL.revokeObjectURL(downloadUrl);

		return true;
	} catch (error) {
		const message = formatAxiosError(
			error,
			"No se pudo descargar el PDF de la factura."
		);

		window.messageApi.error(message, {
			modal: true,
			title: "Error Business Central",
		});

		return false;
	}
};

